import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { cn } from '@/lib/utils'
import type { KnowledgeOptions, Scope } from '../api'

interface PickItem { id: number; label: string; hint?: string }

const SHOWN = 60

/** A searchable list in a popover: an estate has hundreds of objects, too many for a plain select. */
export function SearchPicker({ items, value, onChange, placeholder, label, testId }: {
  items: PickItem[]; value: number | null; onChange: (id: number) => void; placeholder: string; label: string; testId?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const picked = items.find((i) => i.id === value) ?? null
  const shown = useMemo(() => {
    const q = term.trim().toLowerCase()
    return (q ? items.filter((i) => i.label.toLowerCase().includes(q) || i.hint?.toLowerCase().includes(q)) : items).slice(0, SHOWN)
  }, [items, term])
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={testId}
          className="flex h-9 w-full items-center justify-between rounded-md border bg-background px-3 text-left text-sm"
        >
          <span className={cn('truncate', !picked && 'text-muted-foreground')}>{picked ? picked.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-72 p-2" align="start">
        <Input
          autoFocus
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t('knowledgeBase.pickerSearch', 'Search')}
          aria-label={t('knowledgeBase.pickerSearch', 'Search')}
          className="mb-2 h-8 text-sm"
        />
        <div role="listbox" aria-label={label} className="max-h-64 overflow-y-auto">
          {shown.length === 0 && <div className="px-2 py-3 text-sm text-muted-foreground">{t('knowledgeBase.pickerNone', 'Nothing matches.')}</div>}
          {shown.map((i) => (
            <button
              key={i.id}
              type="button"
              role="option"
              aria-selected={i.id === value}
              onClick={() => { onChange(i.id); setOpen(false); setTerm('') }}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
            >
              <Check className={cn('h-3.5 w-3.5 shrink-0', i.id === value ? 'opacity-100' : 'opacity-0')} />
              <span className="truncate">{i.label}</span>
              {i.hint && <span className="ml-auto shrink-0 truncate text-xs text-muted-foreground">{i.hint}</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * What a fact applies to (Andrew, 15 Oct 2026): one object - any agent working on it uses the fact -
 * or every object of one technology in the estate, now and later - its own agent uses it.
 */
export function ScopePicker({ value, onChange, options, testId = 'scope' }: {
  value: Scope; onChange: (s: Scope) => void; options: KnowledgeOptions; testId?: string
}) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<'object' | 'technology'>(value.technologyId != null ? 'technology' : 'object')
  const objects = useMemo(() => options.objects.map((o) => ({ id: o.id, label: o.name, hint: o.technologyName ?? undefined })), [options.objects])
  const technologies = useMemo(() => options.technologies.map((x) => ({
    id: x.id, label: x.name, hint: t('knowledgeBase.objectsCount', '{{n}} objects', { n: x.objects }),
  })), [options.technologies, t])
  return (
    <div className="space-y-2">
      <SegmentedControl
        size="sm"
        ariaLabel={t('knowledgeBase.appliesTo', 'Applies to')}
        value={kind}
        onValueChange={(v) => { setKind(v); onChange({ objectId: null, technologyId: null }) }}
        options={[
          { value: 'object', label: t('knowledgeBase.scopeObject', 'One object') },
          { value: 'technology', label: t('knowledgeBase.scopeTechnology', 'A technology') },
        ]}
      />
      {kind === 'object' ? (
        <SearchPicker
          items={objects}
          value={value.objectId}
          onChange={(id) => onChange({ objectId: id, technologyId: null })}
          placeholder={t('knowledgeBase.chooseObject', 'Choose the object')}
          label={t('knowledgeBase.object', 'Object')}
          testId={`${testId}-object`}
        />
      ) : (
        <SearchPicker
          items={technologies}
          value={value.technologyId}
          onChange={(id) => onChange({ objectId: null, technologyId: id })}
          placeholder={t('knowledgeBase.chooseTechnology', 'Choose the technology')}
          label={t('knowledgeBase.technology', 'Technology')}
          testId={`${testId}-technology`}
        />
      )}
      <p className="text-xs text-muted-foreground">
        {kind === 'object'
          ? t('knowledgeBase.scopeObjectHint', 'Any AI agent working on this object uses it.')
          : t('knowledgeBase.scopeTechnologyHint', 'Its own agent uses it for every object of this technology in the estate, including objects added later.')}
      </p>
    </div>
  )
}
